import { attachSharedStyleSheetTo } from "@main/infra/pip-stylesheet";
import { AppError } from "@shared/app-error";

/**
 * §3.3 / §3.10 / §4.7.4: Document Picture-in-Picture API でセッション用 Window を確保する。
 *
 * - セッション開始ボタンの user activation 内で同期的に呼ぶこと (await を挟まない)
 * - クローズ検知は `pagehide` (event.persisted === false) と
 *   `documentPictureInPicture.window` の null 化監視を併用 (§4.7.4)
 *   `enterpictureinpicture` は HTMLVideoElement の入場側イベントなのでクローズ検知には使えない
 * - 機能検出で API が未定義なら PipUnsupported を投げる (§4.4 で利用不可ならコア機能ブロック)
 * - 開いた直後に共有 stylesheet を adopt + 「起動中…」 プレースホルダを差し込む。 中身を
 *   render する側 (mountPipControls) は `body.replaceChildren()` で
 *   プレースホルダを掃除してから mount する
 */

declare global {
  interface Window {
    documentPictureInPicture?: {
      requestWindow(opts?: { width?: number; height?: number }): Promise<Window>;
      readonly window: Window | null;
    };
  }
}

export type PipController = {
  pipWindow: Window;
  /** ユーザーがウィンドウを閉じたら resolve する Promise。 */
  closed: Promise<void>;
  /** プログラム的にクローズする。 */
  close: () => void;
};

export function isDocumentPipSupported(): boolean {
  return typeof window !== "undefined" && "documentPictureInPicture" in window;
}

export async function openPipWindow(width = 320, height = 240): Promise<PipController> {
  const api = window.documentPictureInPicture;
  if (!api) {
    throw new AppError("PipUnsupported", "fatal", "Document Picture-in-Picture API is not available");
  }
  let pipWindow: Window;
  try {
    pipWindow = await api.requestWindow({ width, height });
  } catch (err) {
    throw new AppError(
      "PipRequestFailed",
      "fatal",
      err instanceof Error ? err.message : "requestWindow failed",
      err,
    );
  }

  initPipWindowChrome(pipWindow);

  const closed = new Promise<void>((resolve) => {
    const finish = () => {
      pipWindow.removeEventListener("pagehide", onPagehide);
      resolve();
    };
    const onPagehide = (event: PageTransitionEvent) => {
      if (!event.persisted) finish();
    };
    pipWindow.addEventListener("pagehide", onPagehide, { once: true });
  });

  return {
    pipWindow,
    closed,
    close: () => {
      try {
        pipWindow.close();
      } catch {
        // already closed
      }
    },
  };
}

/**
 * 開いた直後の PiP Window を「素の白い <body>」 のまま放置すると、 caller が中身を render する
 * までの間 (camera の getUserMedia 待ちで数百 ms〜数秒) 壊れた空白パネルに見える。 共有 stylesheet
 * を adopt し、 アプリ背景色 + 「起動中…」 プレースホルダ (`[data-pip-placeholder]`) を入れておく。
 * 中身を render する側は `body.replaceChildren()` でこれを掃除してから mount する。
 *
 * ここは CLAUDE.md「`document.createElement` で UI を組み立てない」 の意図的な例外:
 * infra レイヤで PiP open 直後に 1 回だけ実行され、 この時点ではまだ Solid がこの Window に
 * mount されていない (= `render()` の mount target を先に用意する必要がある)。 配色はトークン
 * (`var(--color-*)`) 経由で、 ハードコード色は使わない。
 */
function initPipWindowChrome(pipWindow: Window): void {
  const doc = pipWindow.document;
  doc.title = "くちピタ";
  attachSharedStyleSheetTo(doc);
  doc.body.style.margin = "0";
  doc.body.style.background = "var(--color-bg)";

  const dot = doc.createElement("div");
  dot.className = "h-12 w-12 rounded-full";
  dot.style.background = "linear-gradient(180deg, var(--color-accent-hot), var(--color-accent))";
  const label = doc.createElement("div");
  label.className = "font-sans font-semibold text-[13px] text-ink-3";
  label.textContent = "起動中…";
  const wrap = doc.createElement("div");
  wrap.dataset.pipPlaceholder = "";
  wrap.className = "fixed inset-0 flex flex-col items-center justify-center gap-3";
  wrap.append(dot, label);
  doc.body.append(wrap);
}
